import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
    EventEmitter,
    ChangeDetectorRef,
    OnDestroy,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ILightWeightUser, ILightWeightUserWithUnits } from '@fi-shared/models';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { IChatConversation, IChatConversationUser } from '@fi-shared/fi-chat/fi-chat';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { ActionType } from '@fi-shared/types';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { ManageUsersStep } from '@fi-shared/fi-chat/fi-chat-conversation-manage-type';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fi-chat-conversation-manage-users',
    templateUrl: './fi-chat-conversation-manage-users.component.html',
    styleUrls: ['./fi-chat-conversation-manage-users.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-manage-users' },
    standalone: false,
})
export class FiChatConversationManageUsersComponent implements OnInit, OnDestroy {
    @Input() conversation: IChatConversation;
    @Input() currentManageUsersStep = ManageUsersStep.Remove;
    @Input() addUsers$ = new Observable();
    @Output() close = new EventEmitter();
    @Output() selectedUsersChange = new EventEmitter<number>();
    loading$ = new BehaviorSubject<boolean>(true);
    availableUsersWithUnits: ILightWeightUserWithUnits[] = [];
    selectedUsersToAdd: ILightWeightUserWithUnits[] = [];
    manageUsersStep = ManageUsersStep;
    disabledForRemovingUserIds: number[];

    constructor(
        private fiChatService: FiChatService,
        private fiChatHelperService: FiChatHelperService,
        private userDataService: FiUserDataService,
        private fiDialogsService: FiDialogsService,
        private appText: AppTextService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.getAvailableUsersForConversation();
        this.addUsers$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.onAddingConversationUser());
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected getAvailableForRemovingUsers(): IChatConversationUser[] {
        return this.conversation.Users.filter((user) => !user.IsDeleted);
    }

    getAvailableUsersForConversation() {
        this.fiChatService.getAvailableUsersForConversation().then((users) => {
            // https://chainformation.atlassian.net/browse/FI-4073
            // we should show all conversation users in FiChatConversationUsersListComponent
            // but disable removing users we don't have access to
            this.disabledForRemovingUserIds = this.conversation.Users.map((u) => u.Id).filter(
                (userId) => !users.some((userInConversation) => userInConversation.Id === userId),
            );

            this.availableUsersWithUnits = users.filter((user) => {
                return !this.isUserInConversation(user);
            });

            this.loading$.next(false);
        });
    }

    goBack() {
        if (this.isRemoveUsersStep()) {
            this.close.emit();
        } else {
            if (!this.selectedUsersToAdd.length) {
                this.setCurrentStep(this.manageUsersStep.Remove);
            } else {
                this.showGoBackConfirmDialog();
            }
        }
    }

    onSelectedUsersChange() {
        if (this.selectedUsersChange) this.selectedUsersChange.emit(this.selectedUsersToAdd.length);
    }

    showGoBackConfirmDialog() {
        this.fiDialogsService
            .confirm({
                headerText: this.appText.get('common.discard_changes_question'),
                messageText: this.appText.get('chat.confirm_dont_add_members_message'),
                yesButtonText: this.appText.get('common.discard_kasta'),
                config: {
                    restoreFocus: false,
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => {
                this.selectedUsersToAdd = [];
                this.setCurrentStep(this.manageUsersStep.Remove);
                this.cdr.detectChanges();
            });
    }

    setCurrentStep(step: number) {
        this.currentManageUsersStep = step;
    }

    getContentCardTopText(): string {
        return this.isRemoveUsersStep() ? this.appText.get('common.members') : this.appText.get('common.add_members');
    }

    private isUserInConversation(user: ILightWeightUserWithUnits) {
        return this.conversation.Users.some((conversationUser) => conversationUser.Id === user.Id);
    }

    isRemoveUsersStep(): boolean {
        return this.currentManageUsersStep === ManageUsersStep.Remove;
    }

    onAddingConversationUser() {
        if (this.selectedUsersToAdd.length) {
            const selectedUsersNames = this.getUsersFullNamesString(this.selectedUsersToAdd);

            this.fiDialogsService
                .confirm({
                    headerText: this.appText.get('chat.dialog_add_conversation_user_header'),
                    messageText: this.appText.get('chat.dialog_add_conversation_user_message', {
                        '{0}': selectedUsersNames,
                    }),
                })
                .onYesButtonClick(() => {
                    this.fiChatService
                        .changeConversationUsers(this.conversation.Id, this.selectedUsersToAdd, ActionType.Create)
                        .then(() => {
                            this.close.emit();
                        });
                });
        }
    }

    deleteConversationUsers(users: ILightWeightUser[]) {
        const selectedUsersNames = this.getUsersFullNamesString(users);

        this.fiDialogsService
            .confirm({
                headerText: this.appText.get('chat.dialog_remove_conversation_user_header'),
                messageText: this.appText.get('chat.dialog_remove_conversation_user_message', {
                    '{0}': selectedUsersNames,
                }),
            })
            .onYesButtonClick(() => {
                this.fiChatService.changeConversationUsers(this.conversation.Id, users, ActionType.Delete).then(() => {
                    this.close.emit();
                });
            });
    }

    private getUsersFullNamesString(users: IChatConversationUser[]): string {
        return users.map((user) => user.FullName).join(', ');
    }
}
